import { useSelector } from 'react-redux';
import { colors, Icon, Surface, Tooltip, Txt } from '@vst/beam';
import { InfoOutline } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { useScreenResolution } from '@app/hooks';
import { selectHasFiber, selectUserInfo } from '@app/redux';
import { hasUnlimitedUsageMeter, isUnleashedFamily } from '@app/utils';

import { AccountType, PlanCharacteristics } from '@mfe/shared/schema-types';

import VoicePlanDetails from '../AvailablePlans/VoicePlanDetails';
import { FamilyPlan } from '../../types';

import styles from '../PlanCard/styles.module.scss';

type PlanCardBodyProps = {
  characteristics: Pick<
    PlanCharacteristics,
    | 'dataCap'
    | 'downloadSpeed'
    | 'downloadUnits'
    | 'usageMeter'
    | 'offerName'
    | 'productFamily'
    | 'attributes'
    | 'fiberSpeedText'
  >;
  disclosure?: string | null;
  isCollapsibleCurrentPlan?: boolean;
  isVoicePlan?: boolean;
  id: string;
};

const PlanCardBody = ({
  characteristics,
  disclosure,
  isCollapsibleCurrentPlan,
  isVoicePlan = false,
  id,
}: PlanCardBodyProps) => {
  const { t } = useTranslation('ChangePlan');
  const { isExtraSmall, isSmall, isLarge } = useScreenResolution();

  const headingSize = isExtraSmall || isSmall || isLarge ? 'heading4' : 'heading3';

  const { dataCap, downloadSpeed, downloadUnits, usageMeter, offerName, productFamily, attributes } = characteristics;

  const isUnlimited = hasUnlimitedUsageMeter(usageMeter);
  const hasUnleashedFamily = isUnleashedFamily(productFamily, offerName);

  const isFiber = useSelector(selectHasFiber);
  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);
  const isSMB = accountType === AccountType.Smb;
  const isCafPlan = productFamily === FamilyPlan.CAFII || productFamily === FamilyPlan.BusinessCAF;

  const fiberSpeedDescription = characteristics.fiberSpeedText
    ? characteristics.fiberSpeedText?.split(/(\s\d)/)[0]
    : t('fiber.speedsDescription');
  const fiberSpeedValue = `${downloadSpeed} ${downloadUnits}`;

  const features = [...attributes];
  const showCAFText = isVoicePlan && productFamily !== FamilyPlan.BusinessCAF;

  const showDownloadSection = downloadSpeed && downloadUnits && !hasUnleashedFamily && (isFiber || isCafPlan || isSMB);

  const showDataCap = !isFiber && (dataCap || hasUnleashedFamily);

  return (
    <div>
      <Surface
        className={`${styles['data-section']} ${
          showDownloadSection && showDataCap ? styles['data-section-space-around'] : styles['data-section-center']
        }`}
        data-cy="plan-card-body"
      >
        {showDownloadSection &&
          (isFiber ? (
            <div>
              <Txt variant={'smallRegular'}>{fiberSpeedDescription}</Txt>
              <Txt variant={headingSize} className={styles['white-space']}>
                {fiberSpeedValue}
              </Txt>
            </div>
          ) : (
            <Surface data-cy="download-section">
              <Txt variant={headingSize} className={styles['white-space']}>
                {downloadSpeed} {downloadUnits}
              </Txt>
              <Txt variant={'smallRegular'}>{t('downloadSpeed')}</Txt>
            </Surface>
          ))}

        {showDownloadSection && showDataCap && (
          <Surface data-cy="separator" style={{ borderRight: `1px solid ${colors['gray'][200]}` }} />
        )}

        {showDataCap && (
          <Surface data-cy="data-section">
            <Surface className={styles['show-data-cap-container']}>
              <Txt variant={headingSize} className={styles['white-space']}>
                {hasUnleashedFamily ? t('unlimited') : t('dataCapGB', { dataCap })}
              </Txt>
              {disclosure && (
                <Tooltip
                  data-cy="disclosure-tooltip-text"
                  bodyText={disclosure}
                  style={{ textAlign: 'left' }}
                  beakPosition={hasUnleashedFamily ? 'center' : 'end'}
                  position={isCollapsibleCurrentPlan || isUnlimited ? 'bottom' : undefined}
                  enableAutoPositioning
                >
                  <Icon icon={InfoOutline} color="gray_500" size="16px" style={{ marginLeft: '4px' }} />
                </Tooltip>
              )}
            </Surface>
            <Txt
              variant={'smallRegular'}
              style={{
                color: hasUnleashedFamily ? colors['blue'][700] : undefined,
              }}
            >
              {t('highSpeedData')}
            </Txt>
          </Surface>
        )}
      </Surface>
      {isVoicePlan && <VoicePlanDetails showCAFText={showCAFText} features={features} />}
    </div>
  );
};

export default PlanCardBody;
